import React, { ChangeEvent, FC, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
} from '@mui/material';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { createActionPriceListTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { ActionPriceList } from 'models/action';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { Currency } from 'models/country';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';

type AddActionPriceListDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onAddActionPriceList: (priceList: ActionPriceList) => void;
    actionPriceLists: ActionPriceList[];
};
const AddActionPriceListDialog: FC<AddActionPriceListDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onAddActionPriceList,
    actionPriceLists,
}: AddActionPriceListDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings', 'workflow']);
    const [priceListName, setPriceListName] = useState('');
    const [priceListNameError, setPriceListNameError] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrganization = currentOrgVar();
    const defaultCurrency = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CURRENCY,
    );
    const [currency, setCurrency] = useState<Currency>({ code: defaultCurrency || '' } as Currency);

    const [addActionPriceList] = useMutation(createActionPriceListTemplate, {
        variables: {
            actionPriceListName: priceListName,
            currency: currency.code,
        },
        onCompleted: ({ createActionPriceList }) => {
            onAddActionPriceList(createActionPriceList);
            onCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<ActionPriceList>, { data: { createActionPriceList } }) {
            cache.modify({
                fields: {
                    actionPriceLists(priceListRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (priceListRefs instanceof Array) {
                            return [...priceListRefs, toReference(createActionPriceList)];
                        }
                        return [priceListRefs, toReference(createActionPriceList)];
                    },
                },
            });
        },
    });

    const handlePriceListNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPriceListNameError(false);
        if (!event.target.value || actionListNameAlreadyExist(event.target.value)) {
            setPriceListNameError(true);
        }
        setPriceListName(event.target.value);
    };

    const actionListNameAlreadyExist = (name: string) => {
        const actionListNames = actionPriceLists.map((actionList) =>
            actionList.actionPriceListName.replace(/\s/g, '').toLocaleLowerCase(),
        );
        if (actionListNames.includes(name.replace(/\s/g, '').toLocaleLowerCase())) {
            return true;
        }
        return false;
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={dialogOpen}>
            <DialogTitle>{translate('lab-settings:ADD_PRICE_ACTION_LIST')}</DialogTitle>
            <DialogContent>
                <Box mt={1} mb={2}>
                    <Grid container spacing={2}>
                        <Grid item md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="action-price-list-name"
                                    label={translate('lab-settings:PRICE_LIST_NAME')}
                                    required
                                    onChange={handlePriceListNameChange}
                                    error={priceListNameError}
                                    helperText={
                                        priceListNameError && !priceListName
                                            ? translate('workflow:VALIDATION:NAME')
                                            : priceListNameError &&
                                              translate('lab-settings:VALIDATION:PRICE_LIST_ALREADY_EXIST')
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={6} sm={12}>
                            <CurrencyInput
                                currency={currency}
                                onCurrencySelect={(currencyObj) => {
                                    setCurrency(currencyObj);
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button
                    variant="contained"
                    disabled={!priceListName || priceListNameError}
                    onClick={() => addActionPriceList()}
                >
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddActionPriceListDialog;
